import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Lock, ArrowLeft, Loader2, CheckCircle, XCircle } from "lucide-react";

const GBA_LOGO = "https://customer-assets.emergentagent.com/job_prospect-tracker-24/artifacts/aek653mu_GBA%20LOGO%20PNG.png";

const API = process.env.REACT_APP_BACKEND_URL;

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!token) {
      setError(true);
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!password || password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error("Passwords don't match");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/reset-password`, {
        token,
        new_password: password
      });
      setSuccess(true);
      toast.success("Password reset successfully!");
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to reset password";
      if (message.includes("expired") || message.includes("Invalid")) {
        setError(true);
      }
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-[#0B1120] flex flex-col items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8 animate-fade-in">
            <img src={GBA_LOGO} alt="GBA" className="h-20 mx-auto mb-6" />
          </div>

          <div className="bg-[#1E293B] rounded-2xl p-8 border border-slate-700 shadow-xl animate-slide-up text-center">
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="font-heading text-2xl font-semibold text-white mb-4">
              Invalid or Expired Link
            </h2>
            <p className="text-slate-400 mb-6">
              This password reset link is invalid or has expired. Please request a new one.
            </p>
            <Link to="/forgot-password">
              <Button className="w-full bg-gba-green hover:bg-gba-green-hover">
                Request New Link
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-[#0B1120] flex flex-col items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8 animate-fade-in">
            <img src={GBA_LOGO} alt="GBA" className="h-20 mx-auto mb-6" />
          </div>

          <div className="bg-[#1E293B] rounded-2xl p-8 border border-slate-700 shadow-xl animate-slide-up text-center">
            <CheckCircle className="w-16 h-16 text-gba-green mx-auto mb-4" />
            <h2 className="font-heading text-2xl font-semibold text-white mb-4">
              Password Reset!
            </h2>
            <p className="text-slate-400 mb-6">
              Your password has been reset successfully. You can now login with your new password.
            </p>
            <Link to="/login">
              <Button className="w-full bg-gba-green hover:bg-gba-green-hover">
                Go to Login
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B1120] flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo and Header */}
        <div className="text-center mb-8 animate-fade-in">
          <img src={GBA_LOGO} alt="GBA" className="h-20 mx-auto mb-6" />
          <h1 className="font-heading text-3xl font-bold text-white tracking-tight">
            Set New Password
          </h1>
          <p className="text-slate-400 mt-2">
            Enter your new password below
          </p>
        </div>

        {/* Form */}
        <div className="bg-[#1E293B] rounded-2xl p-8 border border-slate-700 shadow-xl animate-slide-up">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-300">
                New Password
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 bg-[#0F172A] border-slate-700 text-white placeholder:text-slate-600"
                  data-testid="reset-password-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-slate-300">
                Confirm Password
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="pl-10 bg-[#0F172A] border-slate-700 text-white placeholder:text-slate-600"
                  data-testid="reset-confirm-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-gba-green hover:bg-gba-green-hover text-white font-bold py-6 text-lg uppercase tracking-wide shadow-glow"
              data-testid="reset-submit-button"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                "Reset Password"
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <Link
              to="/login"
              className="text-slate-400 hover:text-gba-green inline-flex items-center"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
